import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  LinearProgress,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Switch,
  FormControlLabel,
  TextField,
  Slider,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  Lightbulb as LightbulbIcon,
  AutoFixHigh as AutoFixIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Settings as SettingsIcon,
  Analytics as AnalyticsIcon,
  Campaign as CampaignIcon,
  Science as ScienceIcon,
  Recommend as RecommendIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';

const Optimize = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [autoOptimize, setAutoOptimize] = useState(false);
  const [targetMetric, setTargetMetric] = useState(15);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Mock optimization data
  const optimizationOpportunities = [
    {
      type: 'Critical',
      title: 'Low Converting Offers',
      description: '12 offers have conversion rates below 2%',
      impact: 'High',
      effort: 'Medium',
      potentialGain: '+45% conversions',
      color: 'error'
    },
    {
      type: 'Opportunity',
      title: 'Underperforming Affiliates',
      description: '23 affiliates inactive for 30+ days',
      impact: 'Medium',
      effort: 'Low',
      potentialGain: '+$3,200 revenue',
      color: 'warning'
    },
    {
      type: 'Quick Win',
      title: 'Blog SEO Optimization',
      description: '8 posts missing meta descriptions',
      impact: 'Medium',
      effort: 'Low',
      potentialGain: '+25% organic traffic',
      color: 'success'
    },
    {
      type: 'Opportunity',
      title: 'Category Consolidation',
      description: '6 categories have < 3 offers each',
      impact: 'Low',
      effort: 'Medium',
      potentialGain: '+12% user engagement',
      color: 'info'
    }
  ];

  const abTests = [
    {
      name: 'Offer CTA Button Color',
      status: 'Running',
      progress: 67,
      variant: 'Blue vs Red',
      metric: 'Click Rate',
      current: '12.4%',
      target: '15.0%',
      confidence: '85%'
    },
    {
      name: 'Email Subject Lines',
      status: 'Completed',
      progress: 100,
      variant: 'Urgent vs Friendly',
      metric: 'Open Rate',
      current: '18.7%',
      target: '20.0%',
      confidence: '95%'
    },
    {
      name: 'Landing Page Layout',
      status: 'Planning',
      progress: 0,
      variant: 'Sidebar vs Top Nav',
      metric: 'Conversion Rate',
      current: '8.2%',
      target: '10.0%',
      confidence: '-'
    }
  ];

  const optimizationMetrics = {
    conversionRate: { current: 8.2, target: 10.0, trend: '+0.3%' },
    clickThroughRate: { current: 12.4, target: 15.0, trend: '+0.8%' },
    affiliateRetention: { current: 75.3, target: 80.0, trend: '+2.1%' },
    contentEngagement: { current: 7.5, target: 9.0, trend: '+0.5%' }
  };

  const automationRules = [
    {
      name: 'Auto-pause Low Performers',
      description: 'Pause offers with < 1% conversion after 100 clicks',
      active: true,
      triggered: 3
    },
    {
      name: 'Affiliate Re-engagement',
      description: 'Send email to inactive affiliates after 14 days',
      active: false,
      triggered: 0
    },
    {
      name: 'Content Promotion',
      description: 'Auto-promote high-performing blog posts',
      active: true,
      triggered: 8
    }
  ];

  const renderOfferOptimization = () => (
    <Box>
      <Grid container spacing={3}>
        {/* Performance Overview */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Offer Performance
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Conversion Rate: {optimizationMetrics.conversionRate.current}%
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={(optimizationMetrics.conversionRate.current / optimizationMetrics.conversionRate.target) * 100} 
                  sx={{ mt: 1 }}
                />
                <Typography variant="caption" color="success.main">
                  {optimizationMetrics.conversionRate.trend} this week
                </Typography>
              </Box>
              <Button variant="contained" startIcon={<AutoFixIcon />} fullWidth>
                Auto-Optimize Offers
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Optimization Actions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="outlined"
                    startIcon={<ScienceIcon />}
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    A/B Test CTAs
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="outlined"
                    startIcon={<SpeedIcon />}
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    Optimize Load Times
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="outlined"
                    startIcon={<AnalyticsIcon />}
                    fullWidth
                  >
                    Update Targeting
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="outlined"
                    startIcon={<CampaignIcon />}
                    fullWidth
                  >
                    Create Campaigns
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Active A/B Tests */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Active A/B Tests
              </Typography>
              <TableContainer component={Paper} sx={{ border: 1, borderColor: 'divider' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Test Name</TableCell>
                      <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Progress</TableCell>
                      <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Current Metric</TableCell>
                      <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Target</TableCell>
                      <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Confidence</TableCell>
                      <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {abTests.map((test, index) => (
                      <TableRow 
                        key={index}
                        sx={{ 
                          '&:nth-of-type(even)': { backgroundColor: 'grey.25' },
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                          <Typography variant="body2" fontWeight="medium">
                            {test.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {test.variant}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                          <Chip 
                            label={test.status}
                            color={test.status === 'Running' ? 'primary' : test.status === 'Completed' ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                          <Box sx={{ width: 100 }}>
                            <LinearProgress variant="determinate" value={test.progress} />
                            <Typography variant="caption">{test.progress}%</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>{test.current}</TableCell>
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>{test.target}</TableCell>
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>{test.confidence}</TableCell>
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                          <IconButton size="small">
                            {test.status === 'Running' ? <PauseIcon /> : <PlayIcon />}
                          </IconButton>
                          <IconButton size="small">
                            <EditIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderAffiliateOptimization = () => (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Affiliate Engagement Tools
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <RecommendIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Performance Coaching"
                    secondary="Send personalized tips to underperforming affiliates"
                  />
                  <ListItemSecondaryAction>
                    <Button size="small" variant="outlined">Setup</Button>
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CampaignIcon color="info" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Bonus Campaigns"
                    secondary="Create incentive campaigns for top performers"
                  />
                  <ListItemSecondaryAction>
                    <Button size="small" variant="outlined">Create</Button>
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <AnalyticsIcon color="success" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Custom Dashboards"
                    secondary="Provide affiliates with personalized analytics"
                  />
                  <ListItemSecondaryAction>
                    <Button size="small" variant="outlined">Enable</Button>
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Retention Metrics
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  30-Day Retention: {optimizationMetrics.affiliateRetention.current}%
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={optimizationMetrics.affiliateRetention.current} 
                  sx={{ mt: 1 }}
                />
                <Typography variant="caption" color="success.main">
                  {optimizationMetrics.affiliateRetention.trend} improvement
                </Typography>
              </Box>
              
              <Typography variant="body2" gutterBottom>
                Optimize Commission Structure
              </Typography>
              <Slider
                value={targetMetric}
                onChange={(e, value) => setTargetMetric(value)}
                valueLabelDisplay="auto"
                step={1}
                marks
                min={10}
                max={25}
                valueLabelFormat={(value) => `${value}%`}
              />
              <Button variant="contained" sx={{ mt: 2 }} fullWidth>
                Apply New Commission Rate
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderContentOptimization = () => (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                SEO Optimization
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Missing Meta Descriptions"
                    secondary="8 blog posts need optimization"
                  />
                  <Chip label="Fix Now" color="warning" size="small" />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Alt Text for Images"
                    secondary="23 images missing alt text"
                  />
                  <Chip label="Auto-Fix" color="info" size="small" />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Broken Internal Links"
                    secondary="5 links need updating"
                  />
                  <Chip label="Critical" color="error" size="small" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Content Performance
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Engagement Rate: {optimizationMetrics.contentEngagement.current}%
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={(optimizationMetrics.contentEngagement.current / optimizationMetrics.contentEngagement.target) * 100} 
                  sx={{ mt: 1 }}
                />
              </Box>
              <Button variant="outlined" startIcon={<LightbulbIcon />} fullWidth>
                Generate Content Ideas
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Auto-Publishing
              </Typography>
              <FormControlLabel
                control={<Switch checked={autoOptimize} onChange={(e) => setAutoOptimize(e.target.checked)} />}
                label="Auto-optimize content"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Automatically optimize content based on performance data
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderAutomation = () => (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Automation Rules
              </Typography>
              {automationRules.map((rule, index) => (
                <Accordion key={index}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Switch checked={rule.active} size="small" />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1">{rule.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Triggered {rule.triggered} times this month
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary">
                      {rule.description}
                    </Typography>
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      <Button size="small" variant="outlined">Edit Rule</Button>
                      <Button size="small" variant="outlined" color="error">Delete</Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
              <Button variant="contained" startIcon={<AutoFixIcon />} sx={{ mt: 2 }} fullWidth>
                Create New Rule
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Performance Targets
              </Typography>
              <Box sx={{ mb: 3 }}>
                <TextField
                  label="Conversion Rate Target (%)"
                  type="number"
                  value={optimizationMetrics.conversionRate.target}
                  fullWidth
                  margin="normal"
                />
                <TextField
                  label="Click-through Rate Target (%)"
                  type="number"
                  value={optimizationMetrics.clickThroughRate.target}
                  fullWidth
                  margin="normal"
                />
                <TextField
                  label="Affiliate Retention Target (%)"
                  type="number"
                  value={optimizationMetrics.affiliateRetention.target}
                  fullWidth
                  margin="normal"
                />
              </Box>
              <Button variant="contained" fullWidth>
                Update Targets
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const tabContent = [
    renderOfferOptimization,
    renderAffiliateOptimization,
    renderContentOptimization,
    renderAutomation
  ];

  return (
    <DashboardLayout title="Optimize">
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Improve performance with AI-powered optimization tools and insights
          </Typography>
        </Box>

        {/* Optimization Opportunities */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Optimization Opportunities
          </Typography>
          <Grid container spacing={2}>
            {optimizationOpportunities.map((opportunity, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Alert 
                  severity={opportunity.color}
                  action={
                    <Button color="inherit" size="small">
                      Optimize
                    </Button>
                  }
                >
                  <AlertTitle>{opportunity.title}</AlertTitle>
                  {opportunity.description}
                  <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                    <Chip label={`Impact: ${opportunity.impact}`} size="small" />
                    <Chip label={`Effort: ${opportunity.effort}`} size="small" />
                    <Chip label={opportunity.potentialGain} size="small" color="success" />
                  </Box>
                </Alert>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Optimization Tools Tabs */}
        <Paper sx={{ mb: 4 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Offer Optimization" />
            <Tab label="Affiliate Optimization" />
            <Tab label="Content Optimization" />
            <Tab label="Automation" />
          </Tabs>
        </Paper>

        <Box sx={{ py: 3 }}>
          {tabContent[activeTab]()}
        </Box>
      </Container>
    </DashboardLayout>
  );
};

export default Optimize; 